/* Copyright (c) 2020-present CloudIO Inc */

import { createTheme, Direction, Theme } from '@mui/material';
import { green, grey, orange, yellow } from '@mui/material/colors';
import {
  PaletteColor,
  SimplePaletteColorOptions,
} from '@mui/material/styles/createPalette';
import zIndex from 'constants/ZIndex';
import { useMemo } from 'react';
import { DarkModeType } from 'utils/state';
import {
  defaultBackgroundColorAtom,
  defaultBackgroundColorDarkModeAtom,
  textDisabledColorAtom,
  textDisabledColorDarkModeAtom,
  textPrimaryColorAtom,
  textPrimaryColorDarkModeAtom,
  textSecondaryColorAtom,
  textSecondaryColorDarkModeAtom,
} from './Theme';

declare module '@mui/material' {
  // eslint-disable-next-line no-shadow
  interface Theme {
    ioGrey: PaletteColor;
    yellow: PaletteColor;
  }
}

export const primaryPaletteColorOptions: SimplePaletteColorOptions = {
  contrastText: '#fff',
  dark: '#0071EA',
  light: '#4193EC',
  main: '#157CEC',
};

export const secondaryPaletteColorOptions: SimplePaletteColorOptions = {
  contrastText: '#fff',
  dark: '#ff8f00',
  light: '#ffe082',
  main: '#FFC107',
};

export const warningPaletteColorOptions: SimplePaletteColorOptions = {
  contrastText: '#fff',
  dark: orange[700],
  light: orange[300],
  main: orange[500],
};

export const successPaletteColorOptions: SimplePaletteColorOptions = {
  contrastText: '#fff',
  dark: green[700],
  light: green[300],
  main: green[500],
};

const FontFamily = '"Roboto","Helvetica","Arial","sans-serif"';

export function useBaseTheme({
  darkMode,
  defaultBackgroundColor,
  defaultBackgroundColorDarkMode,
  fontFamily = 'var(--fontFamily)',
  fontSize,
  primary,
  secondary,
  success,
  textDisabledColor,
  textDisabledColorDarkMode,
  textPrimaryColor,
  textPrimaryColorDarkMode,
  textSecondaryColor,
  textSecondaryColorDarkMode,
  warning,
  direction = 'ltr',
}: {
  darkMode: DarkModeType;
  defaultBackgroundColor: string;
  defaultBackgroundColorDarkMode: string;
  fontFamily?: string;
  fontSize: number;
  primary: SimplePaletteColorOptions;
  secondary: SimplePaletteColorOptions;
  success: SimplePaletteColorOptions;
  textDisabledColor: string;
  textDisabledColorDarkMode: string;
  textPrimaryColor: string;
  textPrimaryColorDarkMode: string;
  textSecondaryColor: string;
  textSecondaryColorDarkMode: string;
  warning: SimplePaletteColorOptions;
  direction: Direction;
}): Theme {
  let dark = false;
  switch (darkMode) {
    case 'dark':
      dark = true;
      break;
    case 'light':
      dark = false;
      break;
    default:
      break;
  }
  const theme = useMemo(() => {
    const t = createTheme({
      direction,
      typography: {
        fontFamily,
        fontSize,
      },
      zIndex: {
        modal: zIndex.Model,
        drawer: zIndex.Model,
        tooltip: zIndex.Tooltip,
      },
      palette: {
        mode: dark ? 'dark' : 'light',
        primary,
        secondary,
        success,
        warning,
        background: {
          default: dark
            ? defaultBackgroundColorDarkMode
            : defaultBackgroundColor,
          paper: dark ? '#121212' : '#fff',
        },
        text: {
          primary: dark ? textPrimaryColorDarkMode : textPrimaryColor,
          secondary: dark ? textSecondaryColorDarkMode : textSecondaryColor,
          disabled: dark ? textDisabledColorDarkMode : textDisabledColor,
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              userSelect: 'none',
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              userSelect: 'none',
            },
          },
        },
      },
    });
    t.ioGrey = t.palette.augmentColor({ color: grey, name: 'ioGrey' });
    t.ioGrey.contrastText = '#fff';
    t.yellow = t.palette.augmentColor({ color: yellow, name: 'yellow' });
    t.yellow.contrastText = '#444';
    document.documentElement.style.setProperty(
      '--thumbBG',
      dark ? t.palette.divider : 'rgba(100, 100, 100, 0.4)',
    );
    document.documentElement.style.setProperty(
      '--defaultBG',
      t.palette.background.default,
    );
    document.documentElement.style.setProperty(
      '--paperBG',
      t.palette.background.paper,
    );
    document.documentElement.style.setProperty('--divider', t.palette.divider);
    document.documentElement.style.setProperty(
      '--primary',
      t.palette.primary.main,
    );
    document.documentElement.style.setProperty(
      '--text',
      t.palette.text.primary,
    );
    document.documentElement.style.setProperty(
      '--textSecondary',
      t.palette.text.secondary,
    );
    document.documentElement.style.setProperty(
      '--fontFamily',
      t.typography.fontFamily ?? FontFamily,
    );
    // full calendar css variables
    document.documentElement.style.setProperty(
      '--fc-page-bg-color',
      t.palette.background.default,
    );
    document.documentElement.style.setProperty(
      '--fc-neutral-bg-color',
      t.palette.background.default,
    );
    document.documentElement.style.setProperty(
      '--fc-border-color',
      t.palette.divider,
    );
    document.documentElement.style.setProperty(
      '--fc-neutral-text-color',
      t.palette.text.primary,
    );
    document.documentElement.style.setProperty(
      '--fc-neutral-text-color',
      t.palette.text.primary,
    );
    document.documentElement.style.setProperty(
      '--fc-button-bg-color',
      t.palette.primary.main,
    );
    document.documentElement.style.setProperty(
      '--fc-button-border-color',
      t.palette.primary.dark,
    );
    document.documentElement.style.setProperty(
      '--fc-button-hover-bg-color',
      t.palette.primary.dark,
    );
    document.documentElement.style.setProperty(
      '--fc-button-active-bg-color',
      t.palette.primary.dark,
    );
    document.documentElement.style.setProperty(
      '--fc-event-bg-color',
      t.palette.primary.main,
    );
    document.documentElement.style.setProperty(
      '--fc-event-border-color',
      t.palette.primary.main,
    );
    document.documentElement.style.setProperty(
      '--fc-event-text-color',
      t.palette.primary.contrastText,
    );
    document.documentElement.style.setProperty(
      '--fc-non-business-color',
      t.palette.background.default,
    );
    document.documentElement.style.setProperty(
      '--fc-highlight-color',
      t.palette.action.hover,
    );
    document.documentElement.style.setProperty(
      '--fc-list-event-hover-bg-color',
      t.palette.action.hover,
    );
    document.documentElement.style.setProperty(
      '--fc-today-bg-color',
      yellow['100'],
    );
    // end full calendar css variables
    return t;
  }, [
    dark,
    defaultBackgroundColor,
    defaultBackgroundColorDarkMode,
    direction,
    fontFamily,
    fontSize,
    primary,
    secondary,
    success,
    textDisabledColor,
    textDisabledColorDarkMode,
    textPrimaryColor,
    textPrimaryColorDarkMode,
    textSecondaryColor,
    textSecondaryColorDarkMode,
    warning,
  ]);

  return theme;
}

export function useDefaultTheme() {
  return useBaseTheme({
    darkMode: 'light',
    defaultBackgroundColor: defaultBackgroundColorAtom.defaultValue,
    defaultBackgroundColorDarkMode:
      defaultBackgroundColorDarkModeAtom.defaultValue,
    primary: primaryPaletteColorOptions,
    secondary: secondaryPaletteColorOptions,
    success: successPaletteColorOptions,
    textDisabledColor: textDisabledColorAtom.defaultValue,
    textDisabledColorDarkMode: textDisabledColorDarkModeAtom.defaultValue,
    textPrimaryColor: textPrimaryColorAtom.defaultValue,
    textPrimaryColorDarkMode: textPrimaryColorDarkModeAtom.defaultValue,
    textSecondaryColor: textSecondaryColorAtom.defaultValue,
    textSecondaryColorDarkMode: textSecondaryColorDarkModeAtom.defaultValue,
    warning: warningPaletteColorOptions,
    direction: 'ltr',
    fontSize: 14,
  });
}
